import { Component, HostBinding, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbEntrySearchService } from '@maximizer/core/global-search/data-access';
import { ListItem, Octopus } from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { CaseService } from '@maximizer/outlook/case/data-access';
import {
  AbEntryService,
  NavigationService,
  OutlookService,
  OutlookStore,
  VerticalService,
} from '@maximizer/outlook/shared/data-access';
import {
  CaseForm,
  OutlookAbEntrySearch,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import {
  Subject,
  catchError,
  debounceTime,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';

@Component({
  selector: 'maximizer-outlook-add-case',
  templateUrl: './add-case.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class AddCaseComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  @ViewChild('notification')
  notification!: OutlookNotificationComponent;

  private readonly contactInputSubject = new Subject<string>();

  abEntryKey = '';
  form: FormGroupWrapper<CaseForm>;
  status: ListItem<string>[] = [];
  subject: ListItem<string>[] = [];
  product: ListItem<string>[] = [];
  category: ListItem<string>[] = [];
  contactList: ListItem<string>[] = [];

  isLoading = {
    spinner: false,
    autoComplete: false,
  };

  constructor(
    private readonly translate: TranslateService,
    private readonly caseService: CaseService,
    private readonly route: ActivatedRoute,
    private readonly abEntryService: AbEntryService,
    private readonly abEntrySearchService: AbEntrySearchService,
    private readonly navigationService: NavigationService,
    private readonly outlookService: OutlookService,
    private readonly verticalService: VerticalService,
    public outlookStore: OutlookStore,
  ) {
    this.abEntryKey = decodeURI(this.route.snapshot.paramMap.get('id') ?? '');

    this.form = FormWrapperBuilder.group<CaseForm>({
      key: null,
      subject: [null, [Validators.required, Validators.maxLength(127)]],
      abEntryKey: [null, [Validators.required, Validators.maxLength(9215)]],
      abEntryName: null,
      contactName: null,
      contactKey: null,
      description: [null, Validators.required],
      status: [null, Validators.required],
      contact: null,
      product: [[], Validators.required],
      category: [[]],
    });
  }

  ngOnInit(): void {
    this.isLoading.spinner = true;

    forkJoin({
      fieldOptions: this.caseService.getFieldOptions(),
      abEntry: this.abEntryService.getByKey(this.abEntryKey),
    })
      .pipe(
        map((result) => {
          this.status = [...result.fieldOptions.status].reverse();
          this.subject = result.fieldOptions.subject;
          this.product = result.fieldOptions.product;
          this.category = result.fieldOptions.category;

          this.contactList = [
            { id: this.abEntryKey, name: result.abEntry?.name ?? '' },
          ];

          this.patchFormValues(result.abEntry);
        }),
        catchError((error) => {
          console.error(error);

          return of(null);
        }),
        finalize(() => {
          this.isLoading.spinner = false;
        }),
      )
      .subscribe(() => {
        this.initContactInputSubject();
      });

    this.populateSubject();
  }

  async populateSubject() {
    const subject = await this.outlookService.getSubject(true);

    this.form?.patch({
      subject,
    });

    if (subject.trim() !== '') {
      this.form.group.get('subject')?.markAsTouched();
    }
  }

  private patchFormValues(abEntry: OutlookAbEntrySearch | null): void {
    if (!abEntry) {
      return;
    }
    const defaultStatusId = this.status[0]?.id;

    if (abEntry?.type === Octopus.AbEntryType.Individual) {
      this.form.group.patchValue({
        status: defaultStatusId,
        abEntryKey: this.abEntryKey,
        abEntryName: abEntry?.name ?? '',
      });
    } else {
      this.form.group.patchValue({
        status: defaultStatusId,
        abEntryKey: abEntry?.parentKey ?? this.abEntryKey,
        abEntryName: abEntry?.companyName ?? '',
        contactKey: this.abEntryKey,
        contactName: abEntry?.name ?? '',
      });
    }
  }

  initContactInputSubject() {
    this.contactInputSubject
      .pipe(
        debounceTime(500),
        switchMap((input: string) => {
          if (input.length < 3) {
            this.isLoading.autoComplete = false;
            this.contactList = [];
            return of([]);
          }

          this.isLoading.autoComplete = true;

          return this.abEntrySearchService.search(input, true, 1).pipe(
            catchError((error) => {
              console.error('Autocomplete search failed', error);
              this.contactList = [];
              return of([]);
            }),
            finalize(() => (this.isLoading.autoComplete = false)),
          );
        }),
      )
      .subscribe((data) => {
        this.contactList = data.map((entry) => ({
          id: entry.key ?? '',
          name: entry.name,
        }));
      });
  }

  save(): void {
    if (this.form) {
      this.form.group.markAllAsTouched();
      this.form.group.markAsDirty();

      const validation = this.form.validate();
      const verticalString = this.verticalService.translateVertical('case');

      if (validation.valid) {
        this.isLoading.spinner = true;
        this.caseService
          .save(this.form.value)
          .pipe(
            catchError((error) => {
              this.notification.showMessage(
                'error',
                this.translate.instant('outlook.notification.createError', {
                  type: verticalString,
                }),
              );
              console.error(error);
              return of(null);
            }),
            finalize(() => {
              this.isLoading.spinner = false;
            }),
          )
          .subscribe(() => {
            this.notification.showMessage(
              'success',
              this.translate.instant('outlook.notification.createSuccess', {
                type: verticalString,
              }),
            );
            this.navigationService.navigateBack();
          });
      }
    }
  }

  contactFilterChange(input: string) {
    this.contactList = [];
    this.contactInputSubject.next(input);
  }

  contactValueChange(input: string) {
    const contactData = this.contactList.find((entry) => entry.name === input);
    this.form.patch({
      contactKey: contactData?.id,
      contactName: contactData?.name,
    });
  }

  cancel(): void {
    this.navigationService.navigateBack();
  }
}
